import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyGetSingleShipmentContainerMediaQuery } from "@/src/store/apis/shipments/shipmentDetail";
import { useContainerIdsFullBatchDownloadMutation } from "@/src/store/apis/containerImage";
import { ContainerMediaType } from "@/src/store/apis/tasks/taskDetail/interface";
import { CONTAINER_VALUE_TYPE_CONTAINER_LABEL_TYPE_MAP } from "@/src/pages/exporter/Shipment/components/dialog/imageMapper";
import {
  ContainerPhotosAdapter,
  ContainerListItem,
  ContainerMediaData,
  MediaByPhotoType,
} from "../types";
import { mapContainerMediaResponse } from "./utils/mapContainerMediaResponse";

/**
 * Exporter Single Container 모드용 Adapter
 * - 단일 컨테이너만 조회하는 모드 (Task 상세 페이지 등에서 사용)
 * - Container List 없이 바로 컨테이너 상세 조회
 * - Photo Type별 탭은 비활성화 (단일 컨테이너이므로 의미 없음)
 *
 * @param containerId - 조회할 컨테이너의 ID
 * @param taskId - 해당 컨테이너가 속한 Task의 ID (통합 API 사용 시 필요)
 * @returns ContainerPhotosAdapter - 통합 어댑터 인터페이스
 *
 * @example
 * const adapter = useExporterSingleAdapter(containerId, taskId);
 * await adapter.fetchContainerList(); // 단일 항목 리스트 + 미디어 동시 로드
 */
export const useExporterSingleAdapter = (
  containerId: number,
  taskId?: number,
  shipmentId?: number
): ContainerPhotosAdapter => {
  const { t } = useTranslation();

  // Single mode에서는 container list가 필요 없음 (단일 컨테이너만)
  const [containerList, setContainerList] = useState<ContainerListItem[]>();
  const [containerMedia, setContainerMedia] = useState<ContainerMediaData>();
  const [mediaByPhotoType] = useState<MediaByPhotoType>();

  const [getSingleContainerMedia, singleContainerMediaResult] =
    useLazyGetSingleShipmentContainerMediaQuery();

  const [downloadByContainers, containerDownloadResult] =
    useContainerIdsFullBatchDownloadMutation();

  // Single mode에서는 container list를 단일 항목으로 구성
  const fetchContainerList = useCallback(async () => {
    if (!containerId) return undefined;

    const apiMode = shipmentId ? 'exporter' : 'task';

    try {
      // 단일 컨테이너 정보를 먼저 가져와서 containerNo를 확인
      const response = await getSingleContainerMedia({
        id: containerId,
        mode: apiMode,
        shipmentId: shipmentId,
        taskIds: taskId ? String(taskId) : undefined,
      }).unwrap();
      if (response) {
        const singleItem: ContainerListItem = {
          id: response.id,
          containerNo: response.containerNo ?? null,
        };
        setContainerList([singleItem]);

        // 동시에 containerMedia도 설정
        const mapped = mapContainerMediaResponse(response);
        setContainerMedia(mapped);

        return [singleItem];
      }
      return undefined;
    } catch (error) {
      console.error("[useExporterSingleAdapter] fetchContainerList error:", error);
      return undefined;
    }
  }, [containerId, taskId, shipmentId, getSingleContainerMedia]);

  // Single mode에서는 이미 fetchContainerList에서 데이터를 가져오므로 별도 fetch 불필요
  const fetchContainerMedia = useCallback(
    async (id: number) => {
      const apiMode = shipmentId ? 'exporter' : 'task';

      try {
        const response = await getSingleContainerMedia({
          id,
          mode: apiMode,
          shipmentId: shipmentId,
          taskIds: taskId ? String(taskId) : undefined,
        }).unwrap();
        if (response) {
          const mapped = mapContainerMediaResponse(response);
          setContainerMedia(mapped);
        }
      } catch (error) {
        console.error("[useExporterSingleAdapter] fetchContainerMedia error:", error);
      }
    },
    [getSingleContainerMedia, taskId, shipmentId]
  );

  // Single mode에서는 Photo Type별 조회가 의미 없음 (단일 컨테이너)
  const fetchMediaByPhotoType = useCallback(async () => {
    // Single container mode에서는 Photo Type 탭이 비활성화되므로 no-op
  }, []);

  const downloadByContainerIds = useCallback(
    async (ids: number[]) => {
      if (ids.length === 0) return;
      try {
        await downloadByContainers({ containerIdList: ids }).unwrap();
      } catch (error) {
        console.error("[useExporterSingleAdapter] downloadByContainerIds error:", error);
      }
    },
    [downloadByContainers]
  );

  // Single mode에서는 Photo Type별 다운로드가 의미 없음
  const downloadByPhotoTypes = useCallback(
    async (_types: ContainerMediaType[]) => {
      // Single container mode에서는 container 기반 다운로드 사용
      if (containerId) {
        try {
          await downloadByContainers({ containerIdList: [containerId] }).unwrap();
        } catch (error) {
          console.error("[useExporterSingleAdapter] downloadByPhotoTypes error:", error);
        }
      }
    },
    [containerId, downloadByContainers]
  );

  const getPhotoTypeLabel = useCallback(
    (type: ContainerMediaType): string => {
      const labelMap = CONTAINER_VALUE_TYPE_CONTAINER_LABEL_TYPE_MAP(t);
      return labelMap[type] || type;
    },
    [t]
  );

  return {
    containerList,
    containerMedia,
    mediaByPhotoType,

    isContainerListLoading: singleContainerMediaResult.isFetching,
    isContainerMediaLoading: singleContainerMediaResult.isFetching,
    isMediaByPhotoTypeLoading: false, // Photo Type 탭 비활성화

    fetchContainerList,
    fetchContainerMedia,
    fetchMediaByPhotoType,

    downloadByContainerIds,
    downloadByPhotoTypes,
    isContainerDownloading: containerDownloadResult.isLoading,
    isPhotoTypeDownloading: containerDownloadResult.isLoading,

    getPhotoTypeLabel,
  };
};
